import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { reportCategoryMap } from "./reportConfig";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

const CategoryDropdown = React.memo(
  ({
    categoryKey,
    data,
    selectedData,
    search,
    onSearchChange,
    onDropdownClose,
    onAllToggle,
    onItemSelection,
  }) => {
    const filteredData = useMemo(
      () =>
        data.filter((item) => {
          const searchStr = search.toLowerCase();
          const nameStr = String(item.name ?? "").toLowerCase();
          const displayNameStr = String(item.displayName ?? "").toLowerCase();
          const idStr = String(item.id ?? "").toLowerCase();
          return (
            nameStr.includes(searchStr) ||
            displayNameStr.includes(searchStr) ||
            idStr.includes(searchStr)
          );
        }),
      [data, search]
    );

    return (
      <Box
        sx={{
          border: "1px solid #183059",
          borderRadius: "12px",
          maxHeight: 240,
          position: "absolute",
          minWidth: 300,
          top: "100%",
          left: 0,
          zIndex: 10,
          backgroundColor: "white",
          boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
          mt: 1, // Margin to align with search bar
          p: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
          <IconButton
            onClick={onDropdownClose}
            size="small"
            sx={{ color: "#183059" }}
          >
            <ClearIcon />
          </IconButton>
        </Box>
        <Box
          sx={{
            overflowY: "auto",
            maxHeight: "calc(240px - 40px)", // Adjust for header and padding
            "&::-webkit-scrollbar": { width: "8px" },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": {
              background: "#888",
              borderRadius: "3px",
            },
            "&::-webkit-scrollbar-thumb:hover": { background: "#666" },
            scrollbarWidth: "thin",
            scrollbarColor: "#888 transparent",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <Checkbox
              checked={
                selectedData.selectedItems.length === data.length &&
                data.length > 0
              }
              onChange={onAllToggle}
              size="small"
              sx={{
                color: "#20A8E0",
                "&.Mui-checked": {
                  color: "#20A8E0",
                  backgroundColor: "#fff",
                },
              }}
            />
            <ListItemText primary={`All ${categoryKey}`} />
          </Box>
          {filteredData.map((item) => (
            <Box
              key={item.id}
              sx={{ display: "flex", alignItems: "center", mb: 1 }}
            >
              <Checkbox
                checked={selectedData.selectedItems.includes(item.name)}
                onChange={() => onItemSelection(item.name)}
                size="small"
                sx={{
                  color: "#20A8E0",
                  "&.Mui-checked": {
                    color: "#20A8E0",
                    backgroundColor: "#fff",
                  },
                }}
              />
              <ListItemText primary={item.displayName} />
            </Box>
          ))}
        </Box>
      </Box>
    );
  }
);

const CategorySelector = ({
  reportType,
  onCategoryChange,
  vehicles = [],
  routes = [],
  drivers = [],
  stops = [],
  users = [],
  initialCategories = {},
}) => {
  const [selectedCategoryKey, setSelectedCategoryKey] = useState("");
  const [selectedCategories, setSelectedCategories] = useState({});
  const [search, setSearch] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);

  const availableCategories = reportCategoryMap[reportType] || [];

  const categoryDataMap = useMemo(
    () => ({
      Routes: routes.map((r) => ({
        id: r.id,
        name: r.name || r.route_name || "",
        displayName: r.name || r.route_name || "",
      })),
      Vehicles: vehicles.map((v) => ({
        id: v.device_id,
        name: v.VehicleNumber || v.device_id || "",
        displayName: v.name || v.VehicleNumber || "",
      })),
      Drivers: drivers.map((d) => ({
        id: d.id,
        name: `${d.first_name || ""} ${d.last_name || ""}`.trim(),
        displayName: `${d.first_name || ""} ${d.last_name || ""}`.trim(),
      })),
      Stops: stops.map((s) => ({
        id: s.id,
        name: s.name || "",
        displayName: s.name || "",
      })),
      Users: Array.isArray(users)
        ? users.map((u) => ({
            id: u.id,
            name: u.name || u.userName || u.email || "",
            displayName: u.name || u.userName || u.email || "",
          }))
        : [],
    }),
    [routes, vehicles, drivers, stops, users]
  );

  // useEffect(() => {
  //   setSelectedCategoryKey("");
  //   setSelectedCategories({});
  //   setSearch("");
  //   setShowDropdown(false);
  //   onCategoryChange({});
  // }, [reportType, onCategoryChange]);

  const prevReportTypeRef = useRef();

  useEffect(() => {
    if (
      prevReportTypeRef.current !== undefined &&
      prevReportTypeRef.current !== reportType
    ) {
      // Only clear when reportType changes, not on initial mount or re-renders
      setSelectedCategoryKey("");
      setSelectedCategories({});
      setSearch("");
      setShowDropdown(false);
      onCategoryChange({});
    }
    prevReportTypeRef.current = reportType;
  }, [reportType]);

  useEffect(() => {
    if (Object.keys(initialCategories).length > 0) {
      setSelectedCategories(initialCategories);
      const categoryKey = Object.keys(initialCategories)[0];
      if (categoryKey) {
        setSelectedCategoryKey(categoryKey);
      }
    }
  }, [initialCategories]);

  const handleCategoryKeyChange = useCallback(
    (newCategoryKey) => {
      setSelectedCategoryKey(newCategoryKey);
      const data = categoryDataMap[newCategoryKey] || [];
      const newCategory = {
        [newCategoryKey]: {
          isAllSelected: true,
          selectedItems: data.map((item) => item.name),
          required:
            availableCategories.find((c) => c.key === newCategoryKey)
              ?.required || false,
        },
      };
      setSelectedCategories(newCategory);
      setSearch("");
      setShowDropdown(false);
      onCategoryChange(newCategory);
    },
    [availableCategories, categoryDataMap, onCategoryChange]
  );

  const debouncedSearchChange = useMemo(
    () =>
      debounce((value) => {
        setSearch(value);
      }, 300),
    []
  );

  const handleSearchChange = useCallback(
    (e) => {
      const value = e.target.value;
      debouncedSearchChange(value);
      setShowDropdown(true);
    },
    [debouncedSearchChange]
  );

  const handleDropdownClose = useCallback(() => {
    setSearch("");
    setShowDropdown(false);
  }, []);

  const handleSearchIconClick = useCallback(() => {
    setShowDropdown(true);
  }, []);

  const handleAllToggle = useCallback(() => {
    const data = categoryDataMap[selectedCategoryKey] || [];
    const selectedData = selectedCategories[selectedCategoryKey] || {
      isAllSelected: false,
      selectedItems: [],
      required:
        availableCategories.find((c) => c.key === selectedCategoryKey)
          ?.required || false,
    };

    const allSelected =
      selectedData.selectedItems.length === data.length && data.length > 0;
    const newSelectedItems = allSelected ? [] : data.map((item) => item.name);

    const newCategoryData = {
      [selectedCategoryKey]: {
        ...selectedData,
        isAllSelected: !allSelected,
        selectedItems: newSelectedItems,
      },
    };

    setSelectedCategories(newCategoryData);
    onCategoryChange(newCategoryData);
  }, [
    selectedCategoryKey,
    categoryDataMap,
    selectedCategories,
    availableCategories,
    onCategoryChange,
  ]);

  const handleItemSelection = useCallback(
    (itemName) => {
      const selectedData = selectedCategories[selectedCategoryKey] || {
        isAllSelected: false,
        selectedItems: [],
        required:
          availableCategories.find((c) => c.key === selectedCategoryKey)
            ?.required || false,
      };

      const data = categoryDataMap[selectedCategoryKey] || [];
      let newSelectedItems;

      if (selectedData.selectedItems.includes(itemName)) {
        newSelectedItems = selectedData.selectedItems.filter(
          (i) => i !== itemName
        );
      } else {
        newSelectedItems = [...selectedData.selectedItems, itemName];
      }

      const newCategoryData = {
        [selectedCategoryKey]: {
          ...selectedData,
          isAllSelected: newSelectedItems.length === data.length,
          selectedItems: newSelectedItems,
        },
      };

      setSelectedCategories(newCategoryData);
      onCategoryChange(newCategoryData);
    },
    [
      selectedCategoryKey,
      selectedCategories,
      categoryDataMap,
      availableCategories,
      onCategoryChange,
    ]
  );

  const selectedData = selectedCategories[selectedCategoryKey] || {
    isAllSelected: false,
    selectedItems: [],
    required:
      availableCategories.find((c) => c.key === selectedCategoryKey)
        ?.required || false,
  };

  useEffect(() => {
    console.log("Stops prop:", stops);
  }, [stops]);

  return (
    <Box
      className="category-selector"
      sx={{
        display: "flex",
        gap: 2,
        flexDirection: { xs: "column", sm: "row" },
        alignItems: "center",
      }}
    >
      <FormControl sx={{ minWidth: 250 }}>
        {/* <InputLabel sx={{ backgroundColor: "#fff", mt: "-4px" }}>
          Select an Category
        </InputLabel> */}
        <Select
          value={selectedCategoryKey}
          placeholder="Select an Category"
          displayEmpty
          label="Select an Category"
          onChange={(e) => handleCategoryKeyChange(e.target.value)}
          sx={{
            "& fieldset": {
              border: "none",
            },
            height: "46px",
            border: "1px solid #183059",
            borderRadius: "12px",
            "& .MuiSelect-select": {
              py: 1,
            },
          }}
        >
          <MenuItem value="">Select an Category</MenuItem>
          {availableCategories.map((category) => (
            <MenuItem key={category.key} value={category.key}>
              {category.key}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {selectedCategoryKey && (
        <Box sx={{ minWidth: 300, position: "relative" }}>
          <Box
            sx={{
              minWidth: 300,
              position: "relative",
              borderRadius: "12px",
            }}
          >
            <TextField
              placeholder={`All ${selectedCategoryKey}`}
              variant="outlined"
              size="small"
              fullWidth
              value={
                selectedData.isAllSelected
                  ? `All ${selectedCategoryKey}`
                  : selectedData.selectedItems.length > 0
                  ? selectedData.selectedItems.length <= 3
                    ? selectedData.selectedItems.join(", ")
                    : `${selectedData.selectedItems.length} ${selectedCategoryKey} selected`
                  : search
              }
              onChange={handleSearchChange}
              onClick={handleSearchIconClick}
              sx={{
                mb: 0,
                borderRadius: "12px",
                border: "1px solid #183059",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  "& fieldset": {
                    border: "none",
                  },
                },
              }}
              autoComplete="off"
              InputProps={{
                autoComplete: "off",
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleSearchIconClick}
                      size="small"
                      sx={{ visibility: !showDropdown ? "visible" : "hidden" }}
                    >
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          {showDropdown && (
            <CategoryDropdown
              categoryKey={selectedCategoryKey}
              data={categoryDataMap[selectedCategoryKey] || []}
              selectedData={selectedData}
              search={search}
              onSearchChange={handleSearchChange}
              onDropdownClose={handleDropdownClose}
              onAllToggle={handleAllToggle}
              onItemSelection={handleItemSelection}
            />
          )}
        </Box>
      )}
    </Box>
  );
};

export default CategorySelector;
